import React, { FC } from 'react'

export interface FieldValidation {
  field: string
  validation: () => boolean
}

interface ValidationsProviderContextProps {
  setFieldValidation: (field: FieldValidation) => void
  removeFieldValidation: (field: string) => void
  fieldWithErrors: string[]
}

const ValidationsProviderContext = React.createContext<ValidationsProviderContextProps | null>(null)

interface ValidationsProviderProps {
  children: React.ReactNode
}

const fieldWithErrors = (fieldValidations: FieldValidation[]) => {
  return Object.entries(fieldValidations)
    .filter(([_, field]) => {
      return !field.validation()
    })
    .map(([_, field]) => field.field)
}

export const ValidationsProvider: FC<ValidationsProviderProps> = ({ children }) => {
  const [fieldValidations, setFieldValidations] = React.useState<FieldValidation[]>([])

  const setFieldValidation = (field: FieldValidation) => {
    setFieldValidations((prevFieldValidations) => {
      const existingField = prevFieldValidations.findIndex((f) => f.field === field.field)

      if (existingField !== -1) {
        const updatedFieldValidations = [...prevFieldValidations]
        const existingFieldValidation = updatedFieldValidations[existingField]

        if (existingFieldValidation) {
          existingFieldValidation.validation = field.validation
        }
        return updatedFieldValidations
      } else {
        return [...prevFieldValidations, field]
      }
    })
  }

  const removeFieldValidation = (field: string) => {
    setFieldValidations((prevFieldValidations) => {
      return prevFieldValidations.filter((f) => f.field !== field)
    })
  }

  return (
    <ValidationsProviderContext.Provider
      value={{
        setFieldValidation,
        removeFieldValidation,
        fieldWithErrors: fieldWithErrors(fieldValidations),
      }}
    >
      {children}
    </ValidationsProviderContext.Provider>
  )
}

export const useValidationsProvider = (): ValidationsProviderContextProps => {
  const ValidationsProviderModule = React.useContext(ValidationsProviderContext)

  if (!ValidationsProviderModule)
    throw new Error('useValidationsProvider must be used within an ValidationsProviderModule')

  return ValidationsProviderModule
}
